/**
 * M2 — Craft viewer canvas.
 *
 * Renders all scene manifests as artboards on a shared scrollable canvas
 * (Figma/Pencil/Google Stitch–style). TV (640×360) and controller (200×356)
 * artboards appear side-by-side per scene, with a recursive node-tree renderer
 * that visualises the manifest structure.
 *
 * No back button — mode selection lives in WorkspaceHeader.
 * Gated at mount: only rendered when `flags.craftMode && flags.newUi`.
 */
import type { CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SceneNode, UDim2Axis } from '@volley/crucible/scene-schema/index.js';
import type { CraftSceneEntry } from '@volley/crucible-ipc-types/ipc-channels';

interface CraftViewProps {
  gameId: string;
}

function craftScenesQueryKey(gameId: string) {
  return ['craft:list-scenes', gameId] as const;
}

export function CraftView({ gameId }: CraftViewProps) {
  const query = useQuery<CraftSceneEntry[]>({
    queryKey: craftScenesQueryKey(gameId),
    queryFn: () =>
      window.crucible.invoke('craft:list-scenes', { gameId }) as Promise<CraftSceneEntry[]>,
    staleTime: 10_000,
    retry: false,
  });

  if (query.isLoading) {
    return (
      <div className="h-full w-full" style={{ background: 'var(--color-crucible-bg)' }}>
        <div className="flex h-full flex-col items-center justify-center gap-5">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/[0.12] border-t-white/50" />
          <p className="text-[15px]" style={{ color: 'var(--text-2)' }}>
            Loading scenes…
          </p>
        </div>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="h-full w-full" style={{ background: 'var(--color-crucible-bg)' }}>
        <div className="flex h-full flex-col items-center justify-center gap-3">
          <p className="text-[15px]" style={{ color: '#ef4444' }}>
            Failed to load scenes.
          </p>
        </div>
      </div>
    );
  }

  if (query.data !== undefined && query.data.length === 0) {
    return (
      <div className="h-full w-full" style={{ background: 'var(--color-crucible-bg)' }}>
        <div className="flex h-full flex-col items-center justify-center gap-3">
          <p className="text-[15px]" style={{ color: 'var(--text-2)' }}>
            No scene manifests found.
          </p>
          <p className="text-center text-[13px]" style={{ color: 'var(--text-3)' }}>
            Add <code className="font-mono">*.scene.json</code> files under{' '}
            <code className="font-mono">packages/shared/src/scenes/</code> to get started.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-auto" style={{ background: 'var(--color-crucible-bg)' }}>
      <div className="flex min-w-max items-start gap-20 p-20">
        {query.data?.map((entry) => (
          <SceneArtboards key={entry.id} entry={entry} />
        ))}
      </div>
    </div>
  );
}

// ── Scene artboard pair ───────────────────────────────────────────────────────

function SceneArtboards({ entry }: { entry: CraftSceneEntry }) {
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex items-end gap-6">
        {entry.manifest ? (
          <>
            <Artboard
              title="TV"
              width={640}
              height={360}
              nodes={entry.manifest.tv.nodes}
            />
            <Artboard
              title="Controller"
              width={200}
              height={356}
              nodes={entry.manifest.controller.nodes}
            />
          </>
        ) : (
          <ArtboardError width={640} height={360} />
        )}
      </div>
      <p className="font-mono text-xs" style={{ color: '#666' }}>
        {entry.id}
      </p>
    </div>
  );
}

function Artboard({
  title,
  width,
  height,
  nodes,
}: {
  title: string;
  width: number;
  height: number;
  nodes: SceneNode[];
}) {
  return (
    <div className="flex flex-col items-start gap-1.5">
      <p className="text-xs" style={{ color: '#666' }}>
        {title}
      </p>
      <div
        className="relative overflow-hidden"
        style={{
          width,
          height,
          background: '#f9fafb',
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
        }}
      >
        {nodes.map((node) => (
          <NodeRenderer key={node.id} node={node} parentW={width} parentH={height} />
        ))}
      </div>
    </div>
  );
}

function ArtboardError({ width, height }: { width: number; height: number }) {
  return (
    <div
      className="flex items-center justify-center text-xs"
      style={{
        width,
        height,
        background: '#2a2020',
        borderRadius: 2,
        border: '1px solid #5c2626',
        color: '#ef4444',
      }}
    >
      Invalid manifest
    </div>
  );
}

// ── Layout helpers ────────────────────────────────────────────────────────────

function udim2ToCss(axis: UDim2Axis | undefined): string | undefined {
  if (!axis) return undefined;
  if (axis.scale !== undefined && axis.offset !== undefined) {
    if (axis.offset === 0) return `${axis.scale * 100}%`;
    return `calc(${axis.scale * 100}% + ${axis.offset}px)`;
  }
  if (axis.scale !== undefined) return `${axis.scale * 100}%`;
  if (axis.offset !== undefined) return `${axis.offset}px`;
  return undefined;
}

// ── Node renderer ─────────────────────────────────────────────────────────────

const NODE_BG: Record<string, string> = {
  text: 'transparent',
  button: '#eff6ff',
  image: '#e5e7eb',
  container: 'transparent',
  'canvas-region': 'transparent',
};

function NodeRenderer({
  node,
  parentW,
  parentH,
}: {
  node: SceneNode;
  parentW: number;
  parentH: number;
}) {
  const layout = node.layout;
  const hasLayout = !!(layout?.x || layout?.y || layout?.size);

  const style: CSSProperties = {
    background: NODE_BG[node.type] ?? 'transparent',
  };

  if (hasLayout) {
    style.position = 'absolute';
    style.left = udim2ToCss(layout?.x) ?? '0';
    style.top = udim2ToCss(layout?.y) ?? '0';
    if (layout?.size?.w) style.width = udim2ToCss(layout.size.w);
    if (layout?.size?.h) style.height = udim2ToCss(layout.size.h);
    if (layout?.anchor) {
      style.transform = `translate(-${layout.anchor[0] * 100}%, -${layout.anchor[1] * 100}%)`;
    }
  } else {
    style.position = 'relative';
    style.width = '100%';
  }

  if (node.type === 'canvas-region') {
    style.backgroundImage =
      'repeating-linear-gradient(45deg, #d1d5db 0, #d1d5db 1px, transparent 0, transparent 50%)';
    style.backgroundSize = '6px 6px';
    style.minHeight = 32;
  }

  const childW =
    layout?.size?.w?.scale != null && layout?.size?.w?.offset == null
      ? parentW * layout.size.w.scale
      : parentW;
  const childH =
    layout?.size?.h?.scale != null && layout?.size?.h?.offset == null
      ? parentH * layout.size.h.scale
      : parentH;

  return (
    <div style={style} title={`[${node.type}] ${node.id}`}>
      {node.type === 'text' && <NodeText node={node} />}
      {node.type === 'button' && <NodeButton node={node} />}
      {node.type === 'image' && <NodeImage node={node} />}
      {node.type === 'canvas-region' && (
        <div
          style={{
            width: '100%',
            height: '100%',
            minHeight: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 9,
            color: '#7c3aed',
            padding: 4,
            textAlign: 'center',
          }}
        >
          {node.componentRef}
        </div>
      )}
      {node.children?.map((child) => (
        <NodeRenderer key={child.id} node={child} parentW={childW} parentH={childH} />
      ))}
    </div>
  );
}

function NodeText({ node }: { node: SceneNode }) {
  // `text` is the canonical prop name in schema v1; `content` is also accepted
  // for compatibility with scene fixtures that use the common "content" key.
  const textVal = node.props?.['text'] ?? node.props?.['content'];
  return (
    <div
      style={{
        padding: '2px 4px',
        fontSize: 11,
        lineHeight: 1.4,
        color: '#1f2937',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {textVal != null ? (
        <PropToken value={textVal} />
      ) : (
        <span style={{ color: '#d1d5db' }}>{node.id}</span>
      )}
    </div>
  );
}

function NodeButton({ node }: { node: SceneNode }) {
  const label = node.props?.['label'] ?? node.id;
  const action = node.onTap?.action;
  return (
    <div
      style={{
        padding: '4px 8px',
        fontSize: 10,
        borderRadius: 3,
        border: '1px solid #bfdbfe',
        background: '#eff6ff',
        display: 'inline-flex',
        flexDirection: 'column',
        gap: 1,
        minWidth: 40,
        minHeight: 20,
      }}
    >
      <span style={{ color: '#1d4ed8', fontWeight: 600 }}>
        <PropToken value={label} />
      </span>
      {action && (
        <span style={{ color: '#7c3aed', fontSize: 9 }}>{action}</span>
      )}
    </div>
  );
}

function NodeImage({ node }: { node: SceneNode }) {
  const src = node.props?.['src'];
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        minWidth: 24,
        minHeight: 24,
        background: '#e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 9,
        color: '#9ca3af',
        overflow: 'hidden',
      }}
    >
      {src ? <PropToken value={src} /> : '▣'}
    </div>
  );
}

/** Renders a binding expression as an amber chip vs a literal value as plain text. */
function PropToken({ value }: { value: string | number | boolean }) {
  if (typeof value === 'string' && value.startsWith('$')) {
    const short = value.replace(/^\$(state|selector|ctx)\./, '');
    return (
      <span
        title={value}
        style={{
          display: 'inline-block',
          background: 'rgba(251, 191, 36, 0.25)',
          color: '#92400e',
          borderRadius: 2,
          padding: '0 3px',
          fontFamily: 'monospace',
          fontSize: 9,
        }}
      >
        {short}
      </span>
    );
  }
  return <>{String(value)}</>;
}
